import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
} from '@mui/material';
import {EntioDataGrid, StatusBadge, getDeviceStatusVariant} from 'src/components/ui';
import {useCallback, useEffect, useMemo, useState} from 'react';
import AddDeviceModal from 'src/components/Modals/AddDevice';
import {AccessGroup} from 'src/apis/autoGenerate/types/accessGroup';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {IntercomDeviceStatusEnum} from 'src/apis/autoGenerate/types/intercomDevice';
import {
  intercomDeviceServiceDelete,
  assignIntercomManager,
  bulkAssignIntercomManagers,
  replaceIntercomManagers,
} from 'src/apis/autoGenerate/services';
import {useCanViewDevice} from 'src/hooks';
import {formatDate, getVal} from 'src/utils/common';
import {getDeviceBuildingOrAmenityName} from 'src/utils/intercomDevice';
import {GridColDef, type DataGridProps, type GridRowParams} from '@mui/x-data-grid';
import {neutral} from 'src/theme';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import message from 'src/components/Message';
import AssignManagerModal from 'src/components/Modals/AssignManager';
import ViewDeviceDrawer from 'src/components/Modals/ViewDevice';
import {OrgUser} from 'src/apis/autoGenerate/types/orgUser';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import InfoIcon from '@mui/icons-material/Info';

export type IntercomDeviceListGridProps = {
  rows: IntercomDevice[];
  loading?: boolean;
  orgId: string;
  canEdit: boolean;
  onRefresh: () => void;
  checkboxSelection?: boolean;
  emptyMessage?: string;
  /** Passed to the underlying DataGrid (e.g. override `noRowsOverlay`) */
  slots?: DataGridProps['slots'];
};

export default function IntercomDeviceListGrid({
  rows,
  loading = false,
  orgId,
  canEdit,
  onRefresh,
  checkboxSelection = true,
  emptyMessage = 'No devices',
  slots: dataGridSlots,
}: IntercomDeviceListGridProps) {
  const [addDeviceModalOpen, setAddDeviceModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IntercomDevice>();
  const [deviceActionIdAnchorEl, setDeviceActionIdAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [assignManagerModalOpen, setAssignManagerModalOpen] = useState(false);
  const [assignManagerDevice, setAssignManagerDevice] = useState<IntercomDevice | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [viewDevice, setViewDevice] = useState<IntercomDevice | null>(null);
  const canViewDevice = useCanViewDevice();

  const deviceActionOpen = Boolean(deviceActionIdAnchorEl);
  const deviceActionId = deviceActionOpen ? 'device-action-popover' : undefined;

  useEffect(() => {
    if (!addDeviceModalOpen) {
      setSelectedDevice(undefined);
    }
  }, [addDeviceModalOpen]);

  const handleDeviceActionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, device: IntercomDevice) => {
      setSelectedDevice(device);
      setDeviceActionIdAnchorEl(event.currentTarget);
    },
    []
  );

  const handleActionClose = useCallback(() => {
    setDeviceActionIdAnchorEl(null);
  }, []);

  const handleDeviceEdit = useCallback(() => {
    setDeviceActionIdAnchorEl(null);
    setAddDeviceModalOpen(true);
  }, []);

  const handleViewDevice = useCallback(() => {
    setDeviceActionIdAnchorEl(null);
    setViewDevice(selectedDevice ?? null);
  }, [selectedDevice]);

  const handleRowDoubleClick = useCallback(
    (params: GridRowParams<IntercomDevice>) => {
      if (!canEdit) return;
      setSelectedDevice(params.row);
      setAddDeviceModalOpen(true);
    },
    [canEdit]
  );

  const handleRemoveDevice = useCallback(() => {
    intercomDeviceServiceDelete(selectedDevice?.id as string).then(() => {
      message.success('Remove Success');
      onRefresh();
    });
  }, [selectedDevice?.id, onRefresh]);

  const handleOpenAssignModal = useCallback((device: IntercomDevice, isEdit: boolean = false) => {
    setAssignManagerDevice(device);
    setIsEditMode(isEdit);
    setAssignManagerModalOpen(true);
  }, []);

  const handleAssignManager = useCallback(
    async (
      users: (OrgUser | Tenant)[],
      userType: 'org' | 'tenant',
      shouldReplace?: boolean
    ) => {
      if (assignManagerDevice && assignManagerDevice.id) {
        try {
          if (users.length > 1 || shouldReplace) {
            const userIds = users
              .map(user => {
                if (userType === 'tenant') {
                  if ((user as Tenant).user?.id) {
                    return (user as Tenant).user!.id;
                  }
                  return user.id;
                }
                return user.id;
              })
              .filter(Boolean) as string[];

            if (shouldReplace || isEditMode) {
              await replaceIntercomManagers(assignManagerDevice.id, {userIds});
              message.success(`Successfully updated managers with ${userIds.length} user(s)`);
            } else {
              const response = await bulkAssignIntercomManagers(assignManagerDevice.id, {userIds});
              if (response.data?.success) {
                const succeeded = response.data.results?.succeeded?.length || 0;
                const failed = response.data.results?.failed?.length || 0;

                if (failed > 0) {
                  message.warning(
                    `Assigned ${succeeded} of ${succeeded + failed} managers. ${failed} failed.`
                  );
                } else {
                  message.success(`Successfully assigned ${succeeded} manager(s)`);
                }
              }
            }
          } else {
            const user = users[0];
            let userId: string | undefined;

            if (userType === 'tenant') {
              if ((user as Tenant).user?.id) {
                userId = (user as Tenant).user!.id;
              } else {
                userId = user.id;
              }
            } else {
              userId = user.id;
            }

            if (!userId) {
              message.error('Invalid user ID for assignment');
              return;
            }

            if (isEditMode) {
              await replaceIntercomManagers(assignManagerDevice.id, {userIds: [userId]});
              message.success('Updated manager successfully.');
            } else {
              await assignIntercomManager(assignManagerDevice.id, userId);
              message.success('Assigned manager successfully.');
            }
          }

          setAssignManagerModalOpen(false);
          setAssignManagerDevice(null);
          setIsEditMode(false);

          setTimeout(() => {
            onRefresh();
          }, 500);
        } catch (err) {
          message.error(`Failed to ${isEditMode ? 'update' : 'assign'} manager(s).`);
        }
      }
    },
    [assignManagerDevice, isEditMode, onRefresh]
  );

  const handleAssignManagerModalClose = useCallback(() => {
    setAssignManagerModalOpen(false);
  }, []);

  const handleAssignManagerModalExited = useCallback(() => {
    setIsEditMode(false);
    setAssignManagerDevice(null);
  }, []);

  const handleDeviceModalConfirm = useCallback(() => {
    setAddDeviceModalOpen(false);
    onRefresh();
  }, [onRefresh]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {field: 'name', headerName: 'Device Name', minWidth: 160},
      {
        field: 'apartment',
        headerName: 'Property',
        width: 180,
        renderCell: val => {
          return <div>{val.value?.name}</div>;
        },
      },
      {
        field: 'building',
        headerName: 'Building/Amenity',
        width: 180,
        renderCell: params => {
          const label = getDeviceBuildingOrAmenityName(params.row as IntercomDevice);
          return <div>{getVal(label)}</div>;
        },
      },
      {
        field: 'accessGroup',
        headerName: 'Access Group',
        minWidth: 240,
        flex: 1,
        renderCell: val => {
          const cacheIds: string[] = [];
          const accessGroup = ((val.value || []) as AccessGroup[]).filter(({id}) => {
            if (!id || cacheIds.includes(id)) {
              return false;
            }
            cacheIds.push(id);
            return true;
          });
          return (
            <>
              {accessGroup.length > 0
                ? accessGroup.map(_ => (
                    <Box
                      key={_.id}
                      sx={{
                        bgcolor: neutral[200],
                        margin: '0 5px 5px 0',
                        padding: '3px',
                        borderRadius: '5px',
                      }}
                    >
                      {_.name}
                    </Box>
                  ))
                : ' -- '}
            </>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 80,
        renderCell: val => {
          const status = val.value;
          return (
            <StatusBadge
              status={getDeviceStatusVariant(status)}
              label={status === IntercomDeviceStatusEnum.Online ? 'Online' : 'Offline'}
            />
          );
        },
      },
      {
        field: 'installedOn',
        headerName: 'Installed On',
        width: 180,
        renderCell: val => {
          return <div>{formatDate(val.value)}</div>;
        },
      },
      {
        field: 'managers',
        headerName: 'Call Recipients',
        minWidth: 280,
        renderHeader: () => (
          <Tooltip
            title="After assigning Call Recipients to this device, any call to 'Reception' from this intercom will be routed to the assigned Call Recipients."
            placement="top"
            arrow
            sx={{
              tooltip: {
                backgroundColor: '#f5f5f9',
                color: 'rgba(0, 0, 0, 0.87)',
                fontSize: '13px',
                border: '1px solid #dadde9',
                maxWidth: 300,
                padding: '8px 12px',
              },
              arrow: {
                color: '#f5f5f9',
                '&:before': {
                  border: '1px solid #dadde9',
                },
              },
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <span>Call Recipients</span>
              <InfoIcon fontSize="small" color="action" />
            </Box>
          </Tooltip>
        ),
        renderCell: params => {
          const device = params.row;
          const managers = device.managers || [];

          if (managers.length === 0) {
            return canEdit ? (
              <Button
                size="small"
                variant="outlined"
                onClick={e => {
                  e.stopPropagation();
                  handleOpenAssignModal(device, false);
                }}
                sx={{
                  '&:focus, &:focus-visible': {
                    outline: 'none',
                    boxShadow: 'none',
                  },
                }}
              >
                Assign
              </Button>
            ) : (
              <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>No recipients assigned</Box>
            );
          }

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 1,
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  maxWidth: '65%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                }}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${managers[0].name} (${managers[0].email})`}
                </Box>
                {managers.length > 1 && (
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      fontStyle: 'italic',
                    }}
                  >
                    Total {managers.length} Recipients
                  </Box>
                )}
              </Box>
              {canEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenAssignModal(device, true);
                  }}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    flexShrink: 0,
                    '&:focus, &:focus-visible': {
                      outline: 'none',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Manage Recipients
                </Button>
              )}
            </Box>
          );
        },
      },
      ...(canEdit || canViewDevice
        ? [
            {
              field: 'id',
              headerName: '',
              width: 60,
              renderCell: (val: {row: IntercomDevice}) => {
                return (
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleDeviceActionClick(e, val.row);
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ];
  }, [canEdit, canViewDevice, handleOpenAssignModal, handleDeviceActionClick]);

  const convertUserType = (backendUserType: 'TENANT_USER' | 'ORG_USER'): 'tenant' | 'org' => {
    return backendUserType === 'ORG_USER' ? 'org' : 'tenant';
  };

  return (
    <Box>
      <Box>
        <EntioDataGrid
          loading={loading}
          rows={rows}
          columns={columns}
          getRowId={row => row.id}
          pageSizeOptions={[10, 20, 50, 100]}
          checkboxSelection={checkboxSelection}
          onRowDoubleClick={handleRowDoubleClick}
          onRowSelectionModelChange={
            checkboxSelection
              ? newSelection => {
                  setSelectedDeviceIds(newSelection as string[]);
                }
              : undefined
          }
          rowSelectionModel={checkboxSelection ? selectedDeviceIds : undefined}
          emptyMessage={emptyMessage}
          slots={dataGridSlots}
        />
      </Box>
      <Popover
        id={deviceActionId}
        open={deviceActionOpen}
        anchorEl={deviceActionIdAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          {canViewDevice && (
            <ListItem disablePadding>
              <ListItemButton onClick={handleViewDevice}>
                <ListItemText primary="View Device" />
              </ListItemButton>
            </ListItem>
          )}
          {canEdit && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={handleDeviceEdit}>
                  <ListItemText primary="Edit Device" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setDeviceActionIdAnchorEl(null);
                    handleRemoveDevice();
                  }}
                >
                  <ListItemText primary="Remove" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Popover>
      {addDeviceModalOpen && (
        <AddDeviceModal
          initData={selectedDevice}
          open={addDeviceModalOpen}
          handleCancel={() => {
            setAddDeviceModalOpen(false);
          }}
          handleConfirm={handleDeviceModalConfirm}
        />
      )}
      <ViewDeviceDrawer
        open={Boolean(viewDevice)}
        device={viewDevice}
        onClose={() => setViewDevice(null)}
      />
      <AssignManagerModal
        open={assignManagerModalOpen}
        onClose={handleAssignManagerModalClose}
        onExited={handleAssignManagerModalExited}
        onConfirm={handleAssignManager}
        onRefresh={() => {
          setTimeout(() => {
            onRefresh();
          }, 500);
        }}
        orgId={orgId}
        deviceId={assignManagerDevice?.id}
        apartmentId={assignManagerDevice?.apartmentId}
        isEditMode={isEditMode}
        currentManagers={
          assignManagerDevice?.managers
            ? assignManagerDevice.managers.map(manager => ({
                user: manager,
                userType: convertUserType(manager.userType),
              }))
            : []
        }
        allowMultiple={true}
        enableBulkOperations={true}
      />
    </Box>
  );
}
